//React
import React from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
//CSS
import "./pageHeader.scss";

/**
 * Page header (ASB-3988): the "top-fold" that 18 views build by hand — grey
 * band, container, h1, optional subtitle and extra content, and optionally the
 * row of tab buttons that hangs below the band. Same class names and geometry
 * as App.scss's `.top-fold` / `.tab-row`, so a view adopts it with no visible
 * change.
 *
 * Tabs: `{ label, active, onClick }` renders a button; `{ label, to }` renders a
 * router NavLink (active from the route). `tabsSpacing` reserves the tab-row
 * space without rendering tabs, for views whose tabs come from elsewhere
 * (Settings' SubNav).
 *
 * Breadcrumb (ASB-4377): `[{ label, to }, ...]` renders the path to the page
 * above the h1, every level but the last a link, the last marked as the
 * current page.
 */
function PageHeader({
	title,
	titleClassName,
	subtitle,
	breadcrumb,
	tabs,
	tabsSpacing,
	children,
	className,
}) {
	const hasTabs = Array.isArray(tabs) && tabs.length > 0;
	const crumbs = Array.isArray(breadcrumb) ? breadcrumb.filter(Boolean) : [];
	return (
		<>
			<div
				className={[
					"top-fold as-page-header",
					subtitle ? "as-page-header--with-subtitle" : "",
					crumbs.length > 0 ? "as-page-header--with-breadcrumb" : "",
					hasTabs || tabsSpacing ? "w-tabs" : "",
					className ?? "",
				]
					.join(" ")
					.trim()}
			>
				<div className="container">
					<div className="container--inner">
						{crumbs.length > 0 ? (
							<nav
								className="as-page-header__breadcrumb"
								aria-label="Breadcrumb"
							>
								<ol>
									{crumbs.map((crumb, i) => (
										<li
											key={`${crumb.label}-${i}`}
											aria-current={i === crumbs.length - 1 ? "page" : undefined}
										>
											{crumb.to && i < crumbs.length - 1 ? (
												<NavLink to={crumb.to}>{crumb.label}</NavLink>
											) : (
												crumb.label
											)}
										</li>
									))}
								</ol>
							</nav>
						) : null}
						<h1 className={titleClassName}>{title}</h1>
						{subtitle ? <p className="p2 as-page-header__subtitle">{subtitle}</p> : null}
						{children}
					</div>
				</div>
			</div>
			{hasTabs ? (
				<div
					className="tab-row container--inner as-page-header__tabs"
					role="tablist"
				>
					{tabs.map((tab) =>
						tab.to ? (
							<NavLink
								key={tab.label}
								to={tab.to}
								className={({ isActive }) =>
									`tab-button ${isActive || tab.active ? "active" : "inactive"}`
								}
								role="tab"
								hidden={tab.hidden}
							>
								{tab.label}
							</NavLink>
						) : (
							<button
								key={tab.label}
								type="button"
								role="tab"
								aria-selected={tab.active ? true : false}
								className={`tab-button ${tab.active ? "active" : "inactive"}`}
								onClick={tab.onClick}
								hidden={tab.hidden}
							>
								{tab.label}
							</button>
						)
					)}
				</div>
			) : null}
		</>
	);
}

PageHeader.propTypes = {
	title: PropTypes.node.isRequired,
	/** e.g. "caps" */
	titleClassName: PropTypes.string,
	subtitle: PropTypes.node,
	/**
	 * the path to this page, above the title: `{ label, to }` per level, the
	 * last being the page itself (rendered as text, never a link)
	 */
	breadcrumb: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.node.isRequired,
			to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
		})
	),
	tabs: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			active: PropTypes.bool,
			onClick: PropTypes.func,
			to: PropTypes.string,
			hidden: PropTypes.bool,
		})
	),
	/** keep the band's tab padding without rendering tabs */
	tabsSpacing: PropTypes.bool,
	/** extra content under the title, inside the band */
	children: PropTypes.node,
	className: PropTypes.string,
};

export default PageHeader;
